import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'sonner';
import { format, differenceInCalendarDays } from 'date-fns';
import {
  Info, Banknote, Link2, Clock, CalendarDays,
  Timer, Sun, Moon, ChevronDown, Smartphone,
} from 'lucide-react';
import { collectInPerson, isTapToPaySupported } from '../utils/tapToPay';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PhoneInput } from './PhoneInput';
import { orgApi } from '../utils/apiV2';
import api from '../utils/api';
import { useOperatorLocale } from '../hooks/useOperatorLocale';

// Quick Add — the "cheap and dirty" booking logger: cash walk-ins, Boatsetter,
// historic backfills. Minimal required fields, org-aware currency/VAT, no
// availability engine involved (logged as a custom booking).

const BOOKING_TYPES = [
  { id: 'hourly', icon: Timer, label: 'Hourly' },
  { id: 'full_day', icon: Sun, label: 'Full day' },
  { id: 'multi_day', icon: Moon, label: 'Multi-day' },
];

const PAY_MODES = [
  { id: 'external', icon: Banknote, title: 'Paid outside', desc: 'Cash, another platform — logged as paid in full.' },
  { id: 'link', icon: Link2, title: 'Payment link', desc: 'Generates a Stripe link — open it or copy it, share it however you like.' },
  { id: 'tap', icon: Smartphone, title: 'Tap to Pay', desc: 'Tap their card on this phone — collected on the spot.' },
  { id: 'none', icon: Clock, title: 'Unpaid for now', desc: 'Just log it — collect later from the manifest.' },
];

const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 6; h <= 22; h++) {
    for (const m of [0, 15, 30, 45]) {
      if (h === 22 && m > 0) break;
      const v = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      out.push({ value: v, label: `${h12}:${String(m).padStart(2, '0')} ${ampm}` });
    }
  }
  return out;
})();

const DURATION_OPTIONS = [
  { value: 30, label: '30 min' }, { value: 60, label: '1 hour' }, { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' }, { value: 150, label: '2.5 hours' }, { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' }, { value: 300, label: '5 hours' }, { value: 360, label: '6 hours' },
  { value: 480, label: '8 hours' },
];

const addMinutes = (hhmm, mins) => {
  const [h, m] = hhmm.split(':').map(Number);
  const t = new Date(2000, 0, 1, h, m + (parseInt(mins, 10) || 60));
  return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
};

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">{children}</p>
);

const DatePickerField = ({ value, onChange, label, minDate, testId }) => (
  <div className="flex-1 min-w-0">
    <Label className="text-xs text-gray-500">{label}</Label>
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="mt-1 flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 h-11 text-sm text-gray-900 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarDays className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {value ? format(value, 'EEE, MMM d, yyyy') : 'Pick a date'}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-auto" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => d && onChange(d)}
          disabled={minDate ? { before: minDate } : undefined}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  </div>
);

export const QuickAddBooking = forwardRef(({ services = [], onCreated, onSubmittingChange }, ref) => {
  const { currencySymbol, taxLabel, isUAE, formatMoney } = useOperatorLocale();
  const [form, setForm] = useState({ serviceName: '', price: '', taxPct: '', name: '', email: '', notes: '' });
  const [selectedChip, setSelectedChip] = useState(null);
  const [bookingType, setBookingType] = useState('hourly');
  const [date, setDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState(60);
  const [partySize, setPartySize] = useState(1);
  const [phone, setPhone] = useState('');
  const [payMode, setPayMode] = useState('external');
  const [notifyGuest, setNotifyGuest] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickService = (svc) => {
    setSelectedChip(svc.id);
    const svcPrice = svc.basePrice ?? svc.price ?? svc.base_price ?? '';
    setForm((f) => ({
      ...f,
      serviceName: svc.name,
      price: String(svcPrice),
      taxPct: String(svc.taxRate ?? svc.tax_rate ?? f.taxPct ?? ''),
    }));
    const mins = svc.duration_minutes || svc.durationMinutes || svc.duration;
    if (mins && DURATION_OPTIONS.some((d) => d.value === mins)) setDuration(mins);
  };

  const price = parseFloat(form.price) || 0;
  const taxes = Math.round(price * ((parseFloat(form.taxPct) || 0) / 100) * 100) / 100;
  const total = Math.round((price + taxes) * 100) / 100;
  const hasContact = !!(form.email.trim() || phone.trim());
  const nights = bookingType === 'multi_day' && endDate ? differenceInCalendarDays(endDate, date) : 0;

  const submit = async () => {
    if (!form.serviceName.trim()) return toast.error('Pick a service or describe the booking');
    if (!date) return toast.error('Pick a date');
    if (bookingType === 'multi_day' && (!endDate || nights < 1)) return toast.error('Pick an end date after the start date');
    if (price <= 0) return toast.error('Enter the booking price');
    if (payMode === 'tap' && !isTapToPaySupported()) {
      return toast.error('Tap to Pay works in the BookingFlows iPhone & Android apps', {
        description: 'Open the app on your phone to tap a card — or pick another payment option.',
      });
    }
    if (submitting) return;
    setSubmitting(true);
    onSubmittingChange?.(true, payMode);
    try {
      const noteBits = {
        external: ' — payment collected outside BookingFlows',
        link: ' — payment link generated for the guest',
        tap: ' — collected in person via Tap to Pay',
        none: '',
      };
      const dateStr = format(date, 'yyyy-MM-dd');
      const typeNote = bookingType === 'multi_day'
        ? `\nMulti-day: ${format(date, 'MMM d')} → ${format(endDate, 'MMM d, yyyy')} (${nights + 1} days)`
        : bookingType === 'full_day' ? '\nFull-day booking' : '';
      const startTime = bookingType === 'hourly' ? time : '09:00';
      const endTime = bookingType === 'hourly' ? addMinutes(time, duration) : '17:00';
      const payload = {
        booking_date: dateStr,
        start_time: startTime,
        end_time: endTime,
        customer_name: form.name.trim() || 'Walk-in guest',
        party_size: Math.max(1, parseInt(partySize, 10) || 1),
        base_price: price,
        taxes,
        total_price: total,
        payment_status: payMode === 'external' ? 'paid' : 'pending',
        amount_paid: payMode === 'external' ? total : 0,
        source: 'admin',
        service_name: form.serviceName.trim(),
        is_custom_booking: true,
        notify_customer: notifyGuest && hasContact,
        internal_notes: `[Quick Add] Logged manually${noteBits[payMode]}.${typeNote}${form.notes.trim() ? `\n${form.notes.trim()}` : ''}`,
      };
      if (form.email.trim()) payload.customer_email = form.email.trim();
      if (phone.trim()) payload.customer_phone = phone.trim();

      const response = await orgApi.createBooking(payload);
      if (response.data?.success || response.data?.booking) {
        const b = response.data.booking || response.data;

        // 💳 Payment link mode: generate a Stripe link for the fresh booking.
        // The booking exists either way — link failure never loses the entry.
        let paymentLink = null;
        if (payMode === 'link' && b.id) {
          try {
            const linkRes = await api.post(`/admin/bookings/${b.id}/generate-payment-link`);
            paymentLink = linkRes.data?.checkoutUrl || null;
            if (!paymentLink) throw new Error('No link returned');
          } catch (linkErr) {
            const d = linkErr.response?.data?.detail;
            toast.error(`Booking added, but the payment link failed: ${typeof d === 'string' ? d : linkErr.message}`);
          }
        }

        // 📲 Tap to Pay mode: collect the full amount in person right now.
        // The booking exists either way — a failed/canceled tap never loses it.
        if (payMode === 'tap' && b.id) {
          const tId = toast.loading('Preparing reader…');
          try {
            await collectInPerson({
              orgApi,
              booking: { id: b.id },
              formatAmount: (a) => formatMoney(a),
              onStatus: (m) => toast.loading(m, { id: tId }),
            });
            toast.success('Paid in full — card tapped ✅', { id: tId });
          } catch (tapErr) {
            if (tapErr?.code === 'canceled') {
              toast.message('Tap canceled — booking saved unpaid', { id: tId, description: 'Collect anytime from the manifest → Collect payment.' });
            } else {
              const d = tapErr?.response?.data?.detail;
              toast.error(typeof d === 'string' ? d : (tapErr.message || 'Tap to Pay could not complete'), { id: tId, description: 'Booking saved unpaid — collect from the manifest.' });
            }
          }
        }

        onCreated?.({
          ...payload,
          customerName: payload.customer_name,
          customerEmail: payload.customer_email,
          customerPhone: payload.customer_phone,
          serviceName: payload.service_name,
          bookingDate: dateStr,
          preferredDate: dateStr,
          startTime,
          totalPrice: total,
          quickAdd: true,
          paymentLink,
          ...b,
          confirmationCode: b.confirmationCode || b.confirmation_code,
        });
      } else {
        throw new Error(response.data?.message || 'Booking creation failed');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : (detail?.message || detail || err.message || 'Could not create booking'));
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false, payMode);
    }
  };

  useImperativeHandle(ref, () => ({ submit }));

  return (
    <div className="space-y-6" data-testid="quick-add-booking">
      {/* ── WHAT ── */}
      <div>
        <SectionLabel>What was booked?</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {services.map((svc) => (
            <button
              key={svc.id}
              type="button"
              onClick={() => pickService(svc)}
              data-testid={`quick-add-service-${svc.id}`}
              className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors ${selectedChip === svc.id ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'}`}
            >
              {svc.name}
              <span className={`ml-1.5 ${selectedChip === svc.id ? 'text-blue-100' : 'text-gray-400'}`}>{formatMoney(svc.basePrice ?? svc.price ?? svc.base_price ?? 0)}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => { setSelectedChip('custom'); set('serviceName', ''); }}
            data-testid="quick-add-service-custom"
            className={`px-3.5 py-2 rounded-full text-xs font-semibold border border-dashed transition-colors ${selectedChip === 'custom' ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'}`}
          >
            + Something else
          </button>
        </div>
        {selectedChip === 'custom' && (
          <Input
            className="mt-2"
            placeholder="Describe it — e.g. Private charter via Boatsetter"
            value={form.serviceName}
            onChange={(e) => set('serviceName', e.target.value)}
            data-testid="quick-add-custom-name"
          />
        )}
      </div>

      {/* ── WHEN ── */}
      <div>
        <SectionLabel>When</SectionLabel>
        <div className="flex gap-2 mb-3">
          {BOOKING_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setBookingType(t.id)}
              data-testid={`quick-add-type-${t.id}`}
              className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-bold border transition-colors ${bookingType === t.id ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <DatePickerField
            value={date}
            onChange={(d) => { setDate(d); if (endDate && d >= endDate) setEndDate(null); }}
            label={bookingType === 'multi_day' ? 'First day' : 'Date'}
            testId="quick-add-date"
          />
          {bookingType === 'multi_day' && (
            <DatePickerField value={endDate} onChange={setEndDate} label="Last day" minDate={date} testId="quick-add-end-date" />
          )}
          {bookingType === 'hourly' && (
            <>
              <div className="flex-1 min-w-0">
                <Label className="text-xs text-gray-500">Start time</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger className="mt-1 h-11" data-testid="quick-add-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {TIME_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-0">
                <Label className="text-xs text-gray-500">Duration</Label>
                <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v, 10))}>
                  <SelectTrigger className="mt-1 h-11" data-testid="quick-add-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {DURATION_OPTIONS.map((d) => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
        {bookingType === 'multi_day' && endDate && nights >= 1 && (
          <p className="text-[11px] text-gray-400 mt-1.5" data-testid="quick-add-multiday-summary">{nights + 1} days · {format(date, 'MMM d')} → {format(endDate, 'MMM d')}</p>
        )}
      </div>

      {/* ── PRICING ── */}
      <div>
        <SectionLabel>Pricing</SectionLabel>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs text-gray-500">Price ({currencySymbol})</Label>
            <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} data-testid="quick-add-price" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">{taxLabel} %</Label>
            <Input type="number" min="0" step="0.01" value={form.taxPct} onChange={(e) => set('taxPct', e.target.value)} placeholder="0" data-testid="quick-add-tax" />
          </div>
          <div className="pb-1.5 text-right">
            <span className="text-xs text-gray-400 block">Total</span>
            <span className="text-lg font-extrabold text-gray-900" data-testid="quick-add-total">{formatMoney(total)}</span>
          </div>
        </div>
      </div>

      {/* ── GUEST — everything optional ── */}
      <div>
        <SectionLabel>Guest <span className="normal-case font-medium text-gray-300">— all optional</span></SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Guest name</Label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Walk-in guest" data-testid="quick-add-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Party size</Label>
            <Input type="number" min="1" value={partySize} onChange={(e) => setPartySize(e.target.value)} data-testid="quick-add-party" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Phone</Label>
            <PhoneInput
              value={phone}
              onChange={(e164) => setPhone(e164)}
              defaultCountry={isUAE ? 'AE' : 'US'}
              data-testid="quick-add-phone"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="guest@email.com" data-testid="quick-add-email" className="h-12" />
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-xs text-gray-500">Notes</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. Cash at the dock · Boatsetter ref #1234" data-testid="quick-add-notes" />
        </div>
        {!hasContact && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mt-3" data-testid="quick-add-contact-note">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>No contact info means BookingFlows can&apos;t send this guest confirmations, tickets or waiver links — communication for this booking will be on you.</span>
          </div>
        )}
      </div>

      {/* ── PAYMENT — quick and dirty, three ways ── */}
      <div>
        <SectionLabel>Collect payment?</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PAY_MODES.filter((m) => m.id !== 'tap' || !isUAE).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setPayMode(m.id)}
              data-testid={`quick-add-pay-${m.id}`}
              className={`text-left rounded-xl border p-3 transition-colors ${payMode === m.id ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 bg-white hover:border-blue-200'}`}
            >
              <p className={`text-xs font-bold flex items-center gap-1.5 ${payMode === m.id ? 'text-blue-700' : 'text-gray-800'}`}>
                <m.icon className="w-3.5 h-3.5" /> {m.title}
                {m.id === 'tap' && !isTapToPaySupported() && (
                  <span className="ml-auto rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-500">In the app</span>
                )}
              </p>
              <p className="text-[10.5px] text-gray-500 mt-1 leading-snug">{m.desc}</p>
            </button>
          ))}
        </div>
        <div className="rounded-xl bg-gray-50 p-4 mt-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Send confirmation to guest</p>
              <p className="text-xs text-gray-500">{hasContact ? 'Emails / messages the guest their booking confirmation.' : 'Add a phone or email above to enable this.'}</p>
            </div>
            <Switch checked={notifyGuest && hasContact} onCheckedChange={setNotifyGuest} disabled={!hasContact} data-testid="quick-add-notify-toggle" />
          </div>
        </div>
      </div>

    </div>
  );
});

QuickAddBooking.displayName = 'QuickAddBooking';

export default QuickAddBooking;
